import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import { useState, type ReactElement } from 'react';
import { Button, Input, Select, Alert } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { listDeals, getDealsQueryKey } from '../../deals/api/deals.client.js';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityRelatedList } from '../../../components/entity-detail/EntityRelatedList.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { formatMoney } from '../../../shared/format/entity-detail.js';
import { DealFormModal } from '../../deals/components/DealFormModal.js';
import { useDeleteDealMutation } from '../../deals/hooks/useDealMutations.js';
import type { ContactDealSummary } from '../api/contacts.client.js';

export function ContactDealsTab({
  contactId,
  deals,
}: {
  contactId: string;
  deals: ContactDealSummary[];
}): ReactElement {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [create, setCreate] = useState(false);
  const [sort, setSort] = useState('title');
  const scope = useCurrentScope();
  const details = useQuery({
    queryKey: getDealsQueryKey(scope?.workspaceId ?? 'anonymous', { contactId }),
    queryFn: () => listDeals({ contactId }),
    enabled: scope !== null && sort === 'updated',
  });
  const sorted = [...deals].sort((a, b) => {
    if (sort === 'amount')
      return Number(b.amount) - Number(a.amount) || a.title.localeCompare(b.title);
    if (sort === 'stage') return a.stage.localeCompare(b.stage) || a.title.localeCompare(b.title);
    if (sort === 'updated') {
      const updated = (id: string): number =>
        Date.parse(details.data?.find((deal) => deal.id === id)?.updatedAt ?? '') || 0;
      return updated(b.id) - updated(a.id) || a.title.localeCompare(b.title);
    }
    return a.title.localeCompare(b.title);
  });
  const remove = useDeleteDealMutation();
  return (
    <>
      {sort === 'updated' && details.error ? (
        <Alert type="error" title={t('crm.entity_detail.load_error')} />
      ) : null}
      <EntityRelatedList
        className="contact-deals-list"
        emptyText={t('crm.entity_detail.empty_deals')}
        filter={
          <div className="contact-deals-filter">
            <Input.Search
              aria-label={t('crm.entity_detail.search')}
              placeholder={t('crm.entity_detail.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select
              size="small"
              aria-label={t('crm.entity_detail.sort')}
              value={sort}
              onChange={setSort}
              style={{ minWidth: 150 }}
              loading={sort === 'updated' && details.isLoading}
              options={['amount', 'stage', 'title', 'updated'].map((value) => ({
                value,
                label: t(`crm.entity_detail.sort_${value}`),
              }))}
            />
          </div>
        }
        action={
          <Button size="small" icon={<PlusOutlined />} onClick={() => setCreate(true)}>
            {t('crm.entity_detail.add_deal')}
          </Button>
        }
        rows={sorted
          .filter((deal) => deal.title.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
          .map((deal) => ({
            id: deal.id,
            title: deal.title,
            href: `/deals/${deal.id}`,
            status: (
              <EntityStatusChip
                tone={
                  deal.stage === 'Won' ? 'success' : deal.stage === 'Lost' ? 'error' : 'processing'
                }
              >
                {getEnumLabel('dealStage', deal.stage, t)}
              </EntityStatusChip>
            ),
            value: (
              <span style={MONO_NUMERIC_STYLE}>{formatMoney(deal.amount, deal.currency)}</span>
            ),
            actions: [
              {
                key: 'delete',
                label: t('crm.entity_detail.delete'),
                danger: true,
                disabled: remove.isPending,
                confirm: {
                  title: t('crm.entity_detail.delete_confirm'),
                  description: t('crm.entity_detail.delete_description'),
                },
                onClick: () => remove.mutateAsync(deal.id),
              },
            ],
          }))}
      />
      {create ? (
        <DealFormModal open lockedContactId={contactId} onClose={() => setCreate(false)} />
      ) : null}
    </>
  );
}
import { MONO_NUMERIC_STYLE } from '../../../shared/ui/list-styles.js';
